/**
 * EIP-712 payload construction for the TP/SL trigger executor.
 *
 * Mirrors the executor's reference signers exactly: `ordersHash` is the keccak
 * of the CONCATENATED leaf hashes (order-sensitive, same order as the `orders`
 * array in the request body), and every price is fixed-18.
 */
import { ethers } from "ethers";
import {
  DEFAULT_TPSL_PRICE_SOURCE,
  TPSL_MARGIN_MODE_UINT8,
  TPSL_PRICE_SOURCE_UINT8,
  TPSL_TRIGGER_TYPE_UINT8,
  TpslLeg,
  TpslMarginMode,
} from "./tpsl.types";

export const TPSL_DOMAIN_NAME = "MarginX TPSL";
export const TPSL_DOMAIN_VERSION = "1";

/** Signature validity window, in seconds. */
export const TPSL_DEADLINE_SECONDS = 300;

export const CREATE_TRIGGERS_TYPES = {
  CreateTriggers: [
    { name: "trader", type: "address" },
    { name: "market", type: "address" },
    { name: "isLong", type: "bool" },
    { name: "marginMode", type: "uint8" },
    { name: "ordersHash", type: "bytes32" },
    { name: "expiresAt", type: "uint256" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

export const CANCEL_TRIGGER_TYPES = {
  CancelTrigger: [
    { name: "trader", type: "address" },
    { name: "triggerId", type: "bytes32" },
    { name: "nonce", type: "uint256" },
    { name: "deadline", type: "uint256" },
  ],
};

/**
 * Leaf v2. The executor still accepts v1 signatures, but ANY leg that carries
 * `priceSource` / `limitPrice` must be v2-signed — and since `priceSource` is
 * now always sent, every leg is signed with this typehash.
 */
export const LEAF_TYPE_STRING =
  "TriggerLeaf(uint8 triggerType,uint256 triggerPrice,uint256 sizeInTokensToClose,uint256 slippageBps,uint8 priceSource,uint256 limitPrice)";

const LEAF_TYPEHASH = ethers.utils.keccak256(
  ethers.utils.toUtf8Bytes(LEAF_TYPE_STRING)
);

/**
 * Render a price as a decimal string the executor parses as a human amount.
 * An integer-only string means WEI to the executor's parser, so a decimal
 * point is always appended.
 */
export function toTriggerPriceString(price: number | string): string {
  const asString = typeof price === "number" ? String(price) : String(price).trim();
  if (!asString || Number.isNaN(Number(asString))) {
    throw new Error("Invalid trigger price");
  }
  // Reject exponent notation before it reaches parseUnits.
  const normalized = asString.includes("e") || asString.includes("E")
    ? Number(asString).toFixed(18).replace(/0+$/, "0")
    : asString;
  return normalized.includes(".") ? normalized : `${normalized}.0`;
}

export function parseFixed18(price: number | string): ethers.BigNumber {
  return ethers.utils.parseUnits(toTriggerPriceString(price), 18);
}

/**
 * A human base-asset amount as the bare fixed-18 INTEGER string that
 * `sizeInTokensToClose` carries on the wire (a bare integer is WEI to the
 * executor's parser, and the leaf hashes it verbatim).
 */
export function toSizeInTokensString(amount: number | string): string {
  return parseFixed18(amount).toString();
}

/**
 * Inverse of `parseFixed18`: the executor's fixed-18 integer string back to a
 * human decimal string ("90000000000000000000000" -> "90000").
 *
 * The two sources of a live trigger price disagree on units — `GET /tpsl/orders`
 * answers the executor's raw fixed-18 integers, while the backend's
 * `positions[].triggers` enrichment has already scaled them. So a value that
 * ALREADY carries a decimal point (or is otherwise not a bare integer) is
 * passed through untouched; only a bare integer is scaled.
 */
export function formatFixed18(raw: string | number | undefined | null): string {
  const text = String(raw ?? "").trim();
  if (!/^-?\d+$/.test(text)) {
    return text;
  }
  return ethers.utils
    .formatUnits(text, 18)
    .replace(/(\.\d*?)0+$/, "$1")
    .replace(/\.$/, "");
}

function leafHash(leg: TpslLeg): string {
  return ethers.utils.keccak256(
    ethers.utils.defaultAbiCoder.encode(
      ["bytes32", "uint8", "uint256", "uint256", "uint256", "uint8", "uint256"],
      [
        LEAF_TYPEHASH,
        TPSL_TRIGGER_TYPE_UINT8[leg.triggerType],
        parseFixed18(leg.triggerPrice),
        // Already fixed-18 on the wire — see the TpslLeg doc comment.
        ethers.BigNumber.from(leg.sizeInTokensToClose || "0"),
        ethers.BigNumber.from(leg.slippageBps),
        TPSL_PRICE_SOURCE_UINT8[leg.priceSource || DEFAULT_TPSL_PRICE_SOURCE],
        // An unset limit price hashes as 0, matching the omitted JSON field.
        leg.limitPrice
          ? parseFixed18(leg.limitPrice)
          : ethers.constants.Zero,
      ]
    )
  );
}

/** keccak256(concat(leafHash(leg) ...)) — ORDER SENSITIVE. */
export function computeOrdersHash(legs: TpslLeg[]): string {
  return ethers.utils.keccak256(
    ethers.utils.hexConcat(legs.map((leg) => leafHash(leg)))
  );
}

/** The trigger UUID's 16 bytes, left-padded to bytes32. */
export function triggerIdToBytes32(triggerId: string): string {
  const hex = triggerId.replace(/-/g, "").toLowerCase();
  if (!/^[0-9a-f]{32}$/.test(hex)) {
    throw new Error("Invalid trigger id");
  }
  return ethers.utils.hexZeroPad(`0x${hex}`, 32);
}

export function marginModeToUint8(marginMode: TpslMarginMode): number {
  return TPSL_MARGIN_MODE_UINT8[marginMode];
}

export function tpslDeadline(): number {
  return Math.floor(Date.now() / 1000) + TPSL_DEADLINE_SECONDS;
}

export function tpslDomain(chainId: number, verifyingContract: string) {
  return {
    name: TPSL_DOMAIN_NAME,
    version: TPSL_DOMAIN_VERSION,
    chainId,
    verifyingContract,
  };
}
